import { createResource, createSignal, For, Show, onMount, onCleanup } from "solid-js";
import { api, type PackDTO, type PackId } from "../lib/api";
import { tg } from "../lib/telegram";
import { t, lang as i18nLang } from "../lib/i18n";
import { impact, notify } from "../lib/haptics";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired after `WebApp.openInvoice` reports `paid`. The parent refetches
   *  the profile so the balance pill updates. The actual credit-grant
   *  happens server-side via the bot webhook \u2014 the callback is purely
   *  the visual signal that the Telegram payment dialog closed OK. */
  onPaid: () => void;
}

export function BuyCreditsModal(props: Props) {
  // Load packs lazily and cache; this is independent of /api/me so the
  // modal stays snappy even if the profile fetch is in-flight.
  const [data] = createResource(
    () => (props.open ? "open" : null),
    () => api.credits(),
  );
  const [buying, setBuying] = createSignal<PackId | null>(null);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  // Lock the body scroll while the modal is open \u2014 matches the IdeaModal
  // pattern so the slot machine doesn't peek through scrolling.
  onMount(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    onCleanup(() => {
      document.body.style.overflow = prev;
    });
  });

  const buy = async (id: PackId) => {
    if (buying()) return;
    setErrorMsg(null);
    setBuying(id);
    impact("medium");
    try {
      const { invoice_link } = await api.buyInvoice(id);
      const app = tg();
      if (!app?.openInvoice) {
        // Outside Telegram (dev / browser) we can't open the native sheet.
        setErrorMsg(t("buy.openInTelegram"));
        return;
      }
      app.openInvoice(invoice_link, (status) => {
        if (status === "paid") {
          notify("success");
          props.onPaid();
          props.onClose();
        } else if (status === "failed") {
          notify("error");
          setErrorMsg(t("buy.failed"));
        }
        // "cancelled" \u2014 silent. "pending" \u2014 the webhook will credit later.
      });
    } catch (err: any) {
      console.error("buy invoice failed", err);
      setErrorMsg(t("buy.failed"));
      notify("error");
    } finally {
      setBuying(null);
    }
  };

  const balance = () => data()?.balance;

  return (
    <Show when={props.open}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => {
          if (e.target === e.currentTarget) props.onClose();
        }}
        style={{
          position: "fixed",
          inset: "0",
          "z-index": 200,
          background: "rgba(0,0,0,0.45)",
          display: "flex",
          "align-items": "flex-end",
          "justify-content": "center",
          "backdrop-filter": "blur(4px)",
        }}
      >
        <div
          class="phone-card"
          style={{
            width: "100%",
            "max-width": "420px",
            background: "#fbfaf6",
            "border-radius": "28px 28px 0 0",
            padding: "calc(var(--app-safe-top) + 20px) 18px calc(28px + var(--app-safe-bottom))",
            "box-shadow": "0 -20px 50px rgba(0,0,0,0.25)",
            "max-height": "90dvh",
            "overflow-y": "auto",
            animation: "slide-up 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        >
          <div class="flex items-center justify-between" style={{ "margin-bottom": "8px" }}>
            <div
              class="serif"
              style={{ "font-size": "22px", "font-weight": 500, "letter-spacing": "-0.3px" }}
            >
              {t("buy.title")}
            </div>
            <button
              onClick={props.onClose}
              aria-label={t("common.close")}
              style={{
                width: "32px",
                height: "32px",
                "border-radius": "16px",
                background: "rgba(0,0,0,0.05)",
                border: "none",
                "font-size": "18px",
                "line-height": 1,
                color: "#444",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={{ "font-size": "13.5px", color: "#5a5a5a", "margin-bottom": "16px" }}>
            {t("buy.subtitle")}
          </div>

          <Show when={balance()}>
            {(b) => (
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  padding: "10px 14px",
                  background: "rgba(248, 243, 230, 0.7)",
                  border: "1px solid rgba(201, 169, 97, 0.25)",
                  "border-radius": "14px",
                  "margin-bottom": "16px",
                  "font-size": "13px",
                }}
              >
                <span>
                  {t("buy.freeLeft", { left: b().free_left, limit: b().free_limit })}
                </span>
                <span style={{ color: "#a89358" }}>·</span>
                <span>{t("buy.creditsLeft", { n: b().credits })}</span>
              </div>
            )}
          </Show>

          <Show when={errorMsg()}>
            <div
              style={{
                "background": "rgba(192, 70, 60, 0.08)",
                "border": "1px solid rgba(192, 70, 60, 0.25)",
                "color": "#c0463c",
                "padding": "10px 14px",
                "border-radius": "12px",
                "margin-bottom": "12px",
                "font-size": "13px",
              }}
            >
              {errorMsg()}
            </div>
          </Show>

          <Show
            when={data()?.packs?.length}
            fallback={
              <div style={{ padding: "24px 0", "text-align": "center", color: "#888" }}>
                {data.loading ? t("buy.loading") : t("buy.openInTelegram")}
              </div>
            }
          >
            <div style={{ display: "flex", "flex-direction": "column", gap: "10px" }}>
              <For each={data()!.packs}>
                {(p) => <PackCard pack={p} buying={buying() === p.id} onBuy={() => buy(p.id)} />}
              </For>
            </div>
          </Show>

          <div
            style={{
              "margin-top": "16px",
              "font-size": "11.5px",
              color: "#999",
              "text-align": "center",
              "line-height": 1.45,
            }}
          >
            {t("buy.footnote")}
          </div>
        </div>
      </div>
    </Show>
  );
}

function PackCard(props: { pack: PackDTO; buying: boolean; onBuy: () => void }) {
  const isBest = props.pack.badge === "best";
  const isWhale = props.pack.badge === "whale";
  const perSpin = (props.pack.stars / props.pack.credits).toFixed(1);
  const ru = i18nLang() === "ru";

  return (
    <button
      onClick={props.onBuy}
      disabled={props.buying}
      style={{
        position: "relative",
        display: "flex",
        "align-items": "center",
        gap: "14px",
        padding: "14px 16px",
        background: isBest
          ? "linear-gradient(135deg, #fff8e0 0%, #fdecc3 100%)"
          : isWhale
            ? "linear-gradient(135deg, #ede0ff 0%, #e0d4ff 100%)"
            : "#fff",
        border: isBest
          ? "1.5px solid #c9a961"
          : isWhale
            ? "1.5px solid #9d7fd9"
            : "1px solid rgba(0,0,0,0.08)",
        "border-radius": "18px",
        cursor: props.buying ? "wait" : "pointer",
        "text-align": "left",
        "font-family": "inherit",
        opacity: props.buying ? 0.7 : 1,
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "box-shadow": isBest
          ? "0 4px 14px rgba(201, 169, 97, 0.25)"
          : "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <Show when={isBest}>
        <div
          style={{
            position: "absolute",
            top: "-9px",
            right: "14px",
            background: "#b8923f",
            color: "#fff",
            padding: "2px 10px",
            "border-radius": "10px",
            "font-size": "10px",
            "font-weight": 700,
            "letter-spacing": "1px",
            "text-transform": "uppercase",
          }}
        >
          {ru ? "Лучшее" : "Best"}
        </div>
      </Show>
      <div
        style={{
          width: "44px",
          height: "44px",
          "border-radius": "22px",
          background: "rgba(255,255,255,0.6)",
          display: "flex",
          "align-items": "center",
          "justify-content": "center",
          "font-size": "24px",
          "flex-shrink": 0,
        }}
      >
        {props.pack.emoji}
      </div>
      <div style={{ flex: 1, "min-width": 0 }}>
        <div
          class="serif"
          style={{ "font-size": "18px", "font-weight": 500, color: "#1a1a1a" }}
        >
          {props.pack.credits} {ru ? "спинов" : "spins"}
        </div>
        <div style={{ "font-size": "12px", color: "#7a7a7a", "margin-top": "2px" }}>
          {perSpin} ⭐ / {ru ? "спин" : "spin"}
        </div>
      </div>
      <div
        style={{
          padding: "8px 14px",
          background: "#1a1a1a",
          color: "#fff",
          "border-radius": "100px",
          "font-size": "14px",
          "font-weight": 600,
          "white-space": "nowrap",
        }}
      >
        {props.buying ? "…" : `${props.pack.stars} ⭐`}
      </div>
    </button>
  );
}
